import React, { useMemo } from "react";
import PlayerCard from "./PlayerCard";
import { EmptyState } from "../../../../components/shared/loading";
import { Users, Calendar } from "lucide-react";

const PlayersView = ({
  players,
  loading,
  error,
  onRetry,
  pagination,
  onPageChange,
  seasons = [],
  selectedLeague,
  selectedSeason,
  onSeasonChange
}) => {
  const seasonOptions = useMemo(() => {
    return seasons.map(s => ({ value: s, label: s.toString() }));
  }, [seasons]);

  const showEmptyState = !selectedLeague || (!loading && (!players || players.length === 0));

  const hasPagination = pagination && pagination.totalPages > 1;
  const pagePlayers = hasPagination && players && players.length > 0
    ? players.slice(
        (pagination.currentPage - 1) * pagination.perPage,
        pagination.currentPage * pagination.perPage
      )
    : players;

  return (
    <div className="space-y-4">
      {/* Season Filter Section */}
      <div className="border border-white/10 rounded-sm p-4 bg-white/[0.02]">
        <div className="max-w-xs">
          <label className="block text-xs font-mono uppercase tracking-widest text-white/60 mb-2">
            Season
          </label>
          <div className="relative">
            <select
              value={selectedSeason}
              onChange={(e) => onSeasonChange(e.target.value)}
              className="w-full px-3 py-2 bg-black border border-white/10 text-white text-sm
                       focus:outline-none focus:border-white/30 transition-colors
                       appearance-none cursor-pointer"
            >
              {seasonOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Content Section */}
      {loading ? (
        <div className="border border-white/10 rounded-sm p-6 bg-white/[0.02]">
          <p className="text-white/70 text-center">Loading players...</p>
        </div>
      ) : error ? (
        <EmptyState
          title="Failed to load players"
          description={error}
          action={
            <button
              onClick={onRetry}
              className="px-4 py-2 bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/30 transition-colors"
            >
              Try Again
            </button>
          }
        />
      ) : showEmptyState ? (
        <EmptyState
          title="No players data available"
          description="Select a league from the filters above to view top scorers"
        />
      ) : (
        <div className="border border-white/10 rounded-sm bg-white/[0.02]">
          <div className="p-6 border-b border-white/10 flex items-center justify-between">
            <h2 className="text-xl font-mono uppercase tracking-widest text-white flex items-center gap-3">
              <Users className="text-white/60" size={20} />
              Top Scorers ({players.length})
            </h2>
            {hasPagination && (
              <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-gray-500">
                Page {pagination.currentPage} of {pagination.totalPages}
              </div>
            )}
          </div>

          <div className="p-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {pagePlayers.map((player) => (
              <PlayerCard key={player?.player?.id} player={player} />
            ))}
          </div>

          {hasPagination && (
            <div className="p-6 border-t border-white/10 flex items-center justify-center gap-3">
              <button
                onClick={() => onPageChange(Math.max(1, pagination.currentPage - 1))}
                disabled={pagination.currentPage === 1}
                className="px-3 py-2 border border-white/10 text-white text-xs font-mono uppercase tracking-widest rounded-sm bg-transparent hover:border-white/30 disabled:opacity-40 transition-all"
              >
                Previous
              </button>
              <span className="px-4 py-2 text-xs font-mono uppercase tracking-[0.3em] text-gray-400">
                {pagination.currentPage} / {pagination.totalPages}
              </span>
              <button
                onClick={() => onPageChange(Math.min(pagination.totalPages, pagination.currentPage + 1))}
                disabled={pagination.currentPage === pagination.totalPages}
                className="px-3 py-2 border border-white/10 text-white text-xs font-mono uppercase tracking-widest rounded-sm bg-transparent hover:border-white/30 disabled:opacity-40 transition-all"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PlayersView;
